// BUILD-042 October 8 21:20 addendum root-cause fix — "file picker is not an understandable
// upload action."
//
// Owner's retest confirmed: a bare <input type="file"> that silently uploads the instant a
// file is chosen gives no indication an action is even available, what file was picked, that
// anything is happening, or whether it succeeded. This shared component is used by BOTH
// DocumentsSelfSection (employee) and AdminDocumentsSection (HR, on an Employee's behalf) so
// the fix is identical in both places: a visible "Choose file" control, the selected filename
// shown as plain text once picked, an explicit, clearly-labeled "Upload signed copy" button
// that only becomes enabled once a file is selected, an in-progress state while the request is
// in flight, and an explicit success or error message afterward — the upload never fires
// merely because a file was picked.
import { useId, useRef, useState } from 'react';

export function DocumentUploadAction({
  onUpload, disabled = false,
}: {
  /** Perform the actual upload for the given File. Resolves/rejects to drive success/error UI. */
  onUpload: (file: File) => Promise<void>;
  disabled?: boolean;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [state, setState] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function handleFileChange(file: File | undefined) {
    setSelectedFile(file ?? null);
    setState('idle');
    setErrorMessage(null);
  }

  async function handleUploadClick() {
    if (!selectedFile || state === 'uploading') return;
    setState('uploading');
    setErrorMessage(null);
    try {
      await onUpload(selectedFile);
      setState('success');
      setSelectedFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e: any) {
      setState('error');
      setErrorMessage(e?.message || 'Upload failed. Please try again.');
    }
  }

  return (
    <div className="flex flex-col gap-1.5 text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <label htmlFor={inputId} className="text-gray-500 flex-shrink-0">Signed copy:</label>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/heic"
          disabled={disabled || state === 'uploading'}
          onChange={(e) => handleFileChange(e.target.files?.[0])}
          className="text-xs"
        />
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={disabled || !selectedFile || state === 'uploading'}
          className="bg-[#4AABDE] text-white text-xs px-3 py-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          {state === 'uploading' ? 'Uploading…' : 'Upload signed copy'}
        </button>
      </div>
      {selectedFile && state !== 'success' && (
        <div className="text-gray-500">
          Selected file: <span className="font-medium text-gray-700">{selectedFile.name}</span>
          {state === 'idle' && ' — click "Upload signed copy" to submit it.'}
        </div>
      )}
      {state === 'uploading' && <div className="text-amber-600">Uploading {selectedFile?.name}…</div>}
      {state === 'success' && <div className="text-green-700">Uploaded. Status is now Awaiting HR review.</div>}
      {state === 'error' && <div className="text-red-700">{errorMessage}</div>}
    </div>
  );
}
